import { api } from "@/lib/api-client";
import type {
  BalanceSnapshot, AllocationRow, RemittanceAllocationConfig,
  BalancePolicy, TransactionRuleConfig, TransactionFieldPromotion, PersistedPolicyAdjustment,
} from "@capture/shared";

export interface StationDocument {
  id: string; displayId: string | null; filename: string; classification: string | null;
  role: "coupon" | "payment" | "attachment" | null; currentStep: string; amountCents: number | null;
  /** SP3 §13 — the field internalName driving `amountCents` (coupon →
   *  appliedAmountField, payment → instrument.amountField). */
  amountFieldName: string | null;
  /** SP7 §7 — effective (keyedValue ?? ocrValue) internalName → value map,
   *  every field on this doc. Feeds the client-side deriveTransactionSnapshot
   *  eval rows so the station's live balance mirrors the server exactly. */
  fields: Record<string, string | null>;
}
export interface StationTransaction {
  id: string; ordinal: number; state: string;
  balanceSnapshot: BalanceSnapshot | null; allocations: AllocationRow[] | null;
  overriddenAt: string | null; duplicateSuspect: boolean; documents: StationDocument[];
  /** SP7 §6 — the durable, row-persisted policy adjustment (survives later
   *  balanced recomputes, unlike `balanceSnapshot.adjustment`). Badge
   *  fallback when the live snapshot carries none. */
  policyAdjustment: PersistedPolicyAdjustment | null;
}
export interface RemittanceStationPayload {
  /** F-013 — jobTypeId lets the station fetch the content-validate bundle so
   *  keying panes get blur-time client validation (same traps as the VS). */
  batch: { id: string; displayId: string | null; customDisplayLabel: string | null; name: string; mode: "combined" | "key" | "balance"; stepType: string; jobTypeId: string };
  config: {
    groupingMode: string; toleranceCents: number; dualControl: boolean;
    allocation?: RemittanceAllocationConfig;
    /** SP7 §7 — mirrors the job's config so the station can run the same
     *  policy engine client-side for the live (unsaved-keystroke) balance. */
    balancePolicy?: BalancePolicy;
    transactionRules?: TransactionRuleConfig[];
    transactionFields?: TransactionFieldPromotion[];
  };
  transactions: StationTransaction[];
  claimedByOther: boolean;
}

export const getStation = (batchId: string) => api.get<RemittanceStationPayload>(`/remittance/batches/${batchId}`);

export const acceptTransaction = (id: string) =>
  api.post<{ status: string; nextStep: string | null; step: string }>(`/transactions/${id}/accept`, {});
export const putAllocations = (id: string, rows: AllocationRow[]) =>
  api.put<{ allocations: AllocationRow[] }>(`/transactions/${id}/allocations`, { rows });
export const applyCheckAmount = (id: string) =>
  api.post<{ snapshot: BalanceSnapshot }>(`/transactions/${id}/apply-check-amount`, {});
export const moveDocument = (id: string, body: { documentId: string; targetTransactionId?: string }) =>
  api.post<{ movedTo: string }>(`/transactions/${id}/move-document`, body);
export const splitTransaction = (id: string, body: { documentId: string }) =>
  api.post<{ newTransactionId: string }>(`/transactions/${id}/split`, body);
export const mergeTransactions = (id: string, body: { targetTransactionId: string }) =>
  api.post<{ mergedInto: string }>(`/transactions/${id}/merge`, body);
export const enterTransaction = (id: string, connectionId: string) =>
  api.post<{ locks: Array<{ documentId: string; acquired: boolean; lockedBy?: string }> }>(`/transactions/${id}/enter`, { connectionId });
export const leaveTransaction = (id: string) =>
  api.post<{ released: number }>(`/transactions/${id}/leave`, {});
/** Reuses the SP2 override route on the batches prefix (design §10 vocab). */
export const overrideTransaction = (batchId: string, id: string, reason: string) =>
  api.post<{ transaction: unknown }>(`/batches/${batchId}/transactions/${id}/override`, { reason });
